<script>
import {
  parse,
  serialize,
  validateSyntax,
} from '../helpers/relationshipParser';
import RelationshipAPI from '../../../../api/relationships';

export default {
  name: 'EditRelatedCompaniesModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contactId: {
      type: [Number, String],
      required: true,
    },
    currentRelationships: {
      type: Array,
      default: () => [],
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text: '',
      syntaxError: '',
      validationResults: [],
      isValidating: false,
      validateDebounceTimer: null,
    };
  },
  computed: {
    modalVisibility: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) this.close();
      },
    },
    canSubmit() {
      return !this.isSubmitting && !this.syntaxError && this.text.trim() !== '';
    },
    statusClasses() {
      return {
        found:
          'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200',
        will_create:
          'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200',
        ambiguous:
          'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200',
        not_found:
          'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200',
        duplicate:
          'bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200',
      };
    },
  },
  watch: {
    show(value) {
      if (value) this.initText();
    },
  },
  beforeUnmount() {
    if (this.validateDebounceTimer) {
      clearTimeout(this.validateDebounceTimer);
    }
  },
  methods: {
    initText() {
      const items = this.currentRelationships.map(rel => ({
        mode: 'reference',
        fields: {
          name: rel.name,
          ...(rel.website_url && { website_url: rel.website_url }),
        },
      }));
      this.text = serialize(items);
      this.syntaxError = '';
      this.validationResults = [];
      this.runClientValidation();
    },
    onInput() {
      this.runClientValidation();
      this.debouncedServerValidation();
    },
    runClientValidation() {
      if (!this.text.trim()) {
        this.syntaxError = '';
        return;
      }
      const result = validateSyntax(this.text);
      this.syntaxError = result.valid
        ? ''
        : result.errors.map(e => e.message).join('; ');
    },
    debouncedServerValidation() {
      if (this.validateDebounceTimer) {
        clearTimeout(this.validateDebounceTimer);
      }
      this.validateDebounceTimer = setTimeout(() => {
        this.runServerValidation();
      }, 300);
    },
    async runServerValidation() {
      if (!this.text.trim() || this.syntaxError) {
        this.validationResults = [];
        return;
      }

      let items;
      try {
        items = parse(this.text);
      } catch {
        this.validationResults = [];
        return;
      }

      if (!items.length) {
        this.validationResults = [];
        return;
      }

      this.isValidating = true;
      try {
        const { data } = await RelationshipAPI.validate({
          entityType: 'contact',
          items,
        });
        this.validationResults = data.results || [];
      } catch {
        this.validationResults = [];
      } finally {
        this.isValidating = false;
      }
    },
    close() {
      this.$emit('close');
    },
    submit() {
      if (!this.canSubmit) return;

      let items;
      try {
        items = parse(this.text);
      } catch {
        return;
      }

      this.$emit('submit', {
        items,
        validationResults: this.validationResults,
      });
    },
    statusLabel(status) {
      const key = `CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.STATUS_${status.toUpperCase()}`;
      return this.$t(key) || status;
    },
  },
};
</script>

<template>
  <woot-modal :show.sync="modalVisibility" :on-close="close">
    <woot-modal-header
      :header-title="$t('CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.TITLE')"
    />
    <div class="flex flex-col gap-4 px-8 pb-8">
      <textarea
        v-model="text"
        rows="6"
        class="mb-0 font-mono text-sm"
        :placeholder="
          $t('CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.PLACEHOLDER')
        "
        @input="onInput"
      />

      <p v-if="syntaxError" class="m-0 text-xs text-red-600">
        {{ syntaxError }}
      </p>

      <div
        v-if="isValidating"
        class="flex items-center gap-2 text-xs text-slate-500"
      >
        <fluent-icon icon="spinner" size="14" class="animate-spin" />
        {{ $t('CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.VALIDATING') }}
      </div>

      <div v-if="validationResults.length" class="flex flex-col gap-1">
        <div
          v-for="(result, i) in validationResults"
          :key="i"
          class="flex items-center gap-2 rounded-md px-2 py-1 text-xs"
          :class="statusClasses[result.status]"
        >
          <span class="font-medium">{{ statusLabel(result.status) }}</span>
          <span v-if="result.matches && result.matches.length === 1">
            {{ result.matches[0].name || result.matches[0].id }}
            <span v-if="result.matches[0].id" class="opacity-60">
              (#{{ result.matches[0].id }})
            </span>
          </span>
          <span v-else-if="result.matches && result.matches.length > 1">
            {{ result.matches.length }} opções
          </span>
          <span v-else-if="result.message" class="opacity-80">{{
            result.message
          }}</span>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <woot-button variant="clear" color-scheme="secondary" @click="close">
          {{ $t('CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.CANCEL') }}
        </woot-button>
        <woot-button
          color-scheme="success"
          :is-loading="isSubmitting"
          :is-disabled="!canSubmit"
          @click="submit"
        >
          {{ $t('CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.SAVE') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>
</template>
